import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DAL_OPTIONS, DalOptions, PublicHttpService } from '@campus/dal';
import { Observable } from 'rxjs';
import { ScopedPaletteResponse } from '../models';

export interface BookScopedPaletteQuery {
  eduContentBookId: number;
  includeChildren?: boolean;
  // When true, the response also carries `learningDomains[]` entries — palettes
  // for the learning domains connected to the first-depth tocs of this book.
  includeLearningDomains?: boolean;
}

export interface TocScopedPaletteQuery {
  eduContentTOCId: number;
  includeChildren?: boolean;
  // When true, the response also carries `learningDomains[]` entries — palettes
  // for the learning domains connected to this toc.
  includeLearningDomains?: boolean;
  // When true, the response also includes palettes for every ancestor toc of
  // the requested toc (and their linked LDs when `includeLearningDomains` is
  // also set). Used to power the deep cascade where a child toc's LD outranks
  // its parent toc.
  includeAncestors?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ScopedPaletteApi {
  private publicHttpService = inject(PublicHttpService);
  private dalOptions = inject<DalOptions>(DAL_OPTIONS);
  private url = `${this.dalOptions.campusApiBaseUrl}/theming/scoped-palette`;

  public getForMethod(methodId: number): Observable<ScopedPaletteResponse> {
    return this.publicHttpService.get<ScopedPaletteResponse>(this.url, buildParams({ methodId }));
  }

  public getForBook(query: BookScopedPaletteQuery): Observable<ScopedPaletteResponse> {
    return this.publicHttpService.get<ScopedPaletteResponse>(this.url, buildParams(query));
  }

  public getForToc(query: TocScopedPaletteQuery): Observable<ScopedPaletteResponse> {
    return this.publicHttpService.get<ScopedPaletteResponse>(this.url, buildParams(query));
  }
}

function buildParams(query: object): HttpParams {
  const fromObject: Record<string, string> = {};

  for (const [key, value] of Object.entries(query)) {
    if (value != null) fromObject[key] = String(value);
  }

  return new HttpParams({ fromObject });
}
